import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { PrivacySettings } from '@models/user';
import { environment } from '../../environments/environment';
import { Document } from '@app/shared/models/documents';
import { AccountService } from './account.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private accountService = inject(AccountService);
    constructor(private http: HttpClient) {}

    sendDocuments(files: File[], documentTypeBase: any, uploadDate: string): Observable<any> {
        const formData = new FormData();
        files.forEach((file, index) => {
            const documentType = parseInt(`${documentTypeBase}_${index}`);
            formData.append('documentType', documentType.toString());
            formData.append('file', file, file.name);
            formData.append('uploadDate', uploadDate);
        });

        return this.http.post(`${environment.apiUrl}/document/upload`, formData, {
            withCredentials: true,
        });
    }

    getDocuments(): Observable<Document[]> {
        return this.http.get<Document[]>(`${environment.apiUrl}/document`);
    }

    updateSourceOfFunds(sourceOfFunds: number): Observable<any> {
        return this.http.put(`${environment.apiUrl}/document?sourceOfFunds=${sourceOfFunds}`, {});
    }

    getOddsChangeType(): Observable<any> {
        return this.http.get(`${environment.apiUrl}/user/overview`, { withCredentials: true }).pipe(
            catchError((error) => {
                console.error('Error fetching odds change type:', error);
                return throwError(() => new Error(error));
            }),
        );
    }

    updateUserPreferences(data: any): Observable<any> {
        return this.http.put(`${environment.apiUrl}/user/user-preferences`, data).pipe(
            tap(() => {
                const currentUser = this.accountService.userSubject.value;
                const oddsFormat =
                    data.oddsFormat !== undefined
                        ? data.oddsFormat == 0
                            ? 'DECIMAL'
                            : 'AMERICAN'
                        : currentUser?.oddsFormat || null;
                if (currentUser) {
                    this.accountService.userSubject.next({
                        ...currentUser,
                        showBalance:
                            data.showBalance !== undefined
                                ? data.showBalance
                                : currentUser.showBalance,
                        oddsFormat: oddsFormat,
                    });
                }
            }),
            catchError((error) => {
                console.error('Error updating odds change type:', error);
                return throwError(() => new Error(error));
            }),
        );
    }

    getFavoriteAmount(): Observable<any> {
        return this.http
            .get(`${environment.apiUrl}/user/user-favourite-amounts`, { withCredentials: true })
            .pipe(
                catchError((error) => {
                    console.error('Error making HTTP request', error);
                    if (error.status === 403) {
                        console.error('Forbidden request:', error.message);
                    }
                    return throwError(() => new Error(error));
                }),
            );
    }

    updateFavoriteAmount(data: any): Observable<any> {
        return this.http
            .put(`${environment.apiUrl}/user/user-favourite-amounts`, data, {
                withCredentials: true,
            })
            .pipe(
                catchError((error) => {
                    console.error('Error making HTTP request', error);
                    if (error.status === 403) {
                        console.error('Forbidden request:', error.message);
                    }
                    return throwError(() => new Error(error));
                }),
            );
    }

    getPrivacySettings(): Observable<PrivacySettings> {
        return this.http
            .get<PrivacySettings>(`${environment.apiUrl}/user/user-privacy-settings`, {
                withCredentials: true,
            })
            .pipe(
                catchError((error) => {
                    console.error('Error fetching privacy settings:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    updatePrivacySettings(data: PrivacySettings): Observable<any> {
        return this.http
            .put(`${environment.apiUrl}/user/user-privacy-settings`, data, {
                withCredentials: true,
            })
            .pipe(
                catchError((error) => {
                    console.error('Error updating privacy settings:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    updatePhoneNumber(data: any): Observable<any> {
        return this.http
            .put(`${environment.apiUrl}/user/personal-details`, data, { withCredentials: true })
            .pipe(
                catchError((error) => {
                    console.error('Error updating phone number:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    updateAddress(data: any): Observable<any> {
        return this.http
            .put(`${environment.apiUrl}/user/personal-details`, data, { withCredentials: true })
            .pipe(
                catchError((error: any) => {
                    console.error('Error updating address:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    updateEmail(data: any): Observable<any> {
        return this.http
            .post(`${environment.apiUrl}/user/request-change-email-verification`, data, {
                withCredentials: true,
            })
            .pipe(
                catchError((error) => {
                    console.error('Error updating email:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    verifyNewEmail(token: string): Observable<any> {
        return this.http
            .post(
                `${environment.apiUrl}/user/verify-new-email`,
                { token },
                { withCredentials: true },
            )
            .pipe(
                catchError((error) => {
                    console.error('Error verifying email:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }

    getWithdrawalCancelations(): Observable<any> {
        return this.http
            .get(`${environment.apiUrl}/payment/user-withdrawal-cancellations`, {
                withCredentials: true,
            })
            .pipe(
                catchError((error) => {
                    console.error('Error fetching Withdrawal Cancelations:', error);
                    if (error.status === 403) {
                        console.error('Forbidden request:', error.message);
                    }
                    return throwError(() => new Error(error));
                }),
            );
    }

    cancelPendingWithdrawal(withdrawalId: number): Observable<any> {
        return this.http
            .post(
                `${environment.apiUrl}/payment/cancel-pending-payout/${withdrawalId}`,
                {},
                { withCredentials: true },
            )
            .pipe(
                catchError((error: any) => {
                    console.error('Error cancelling pending withdrawal:', error);
                    return throwError(() => new Error(error));
                }),
            );
    }
}
